import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Box,
  Button,
  Container,
  Avatar,
  Tooltip,
  useTheme,
  Menu,
  MenuItem,
  Tabs,
  Tab
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import PersonIcon from '@mui/icons-material/Person';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import { logout } from '../../store/actions/authActions';
import { checkInviteEligibility } from '../../store/actions/inviteActions';
import LoginModal from '../Common/LoginModal';
import InviteCodeModal from '../Common/InviteCodeModal';

const AppHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const { eligible } = useSelector((state) => state.invite);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  
  // 导航菜单项
  const navigationItems = [
    { label: '首页', path: '/landing' },
    { label: '证件翻译', path: '/trans' },
    { label: '认证', path: '/certification' },
    { label: '服务', path: '/services' },
    { label: '充值', path: '/pricing' },
    { label: '安全', path: '/security' }
  ];

  // 检查邀请码资格
  useEffect(() => {
    if (isAuthenticated && user) {
      dispatch(checkInviteEligibility()).catch(error => {
        console.error('检查邀请码资格失败:', error);
      });
    }
  }, [isAuthenticated, user, dispatch]);

  // 获取当前选中的tab
  const getCurrentTab = () => {
    const currentPath = location.pathname;
    const index = navigationItems.findIndex(item => item.path === currentPath);
    return index >= 0 ? index : 0;
  };

  // 处理tab切换
  const handleTabChange = (event, newValue) => {
    navigate(navigationItems[newValue].path);
    // 滚动到页面顶部
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // 处理用户菜单打开
  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  // 处理用户菜单关闭
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  // 处理个人中心
  const handleProfile = () => {
    handleCloseMenu();
    navigate('/profile');
  };

  // 处理填写邀请码
  const handleInviteCode = () => {
    handleCloseMenu();
    setShowInviteModal(true);
  };
  
  // 处理退出登录
  const handleLogout = () => {
    handleCloseMenu();
    dispatch(logout());
    navigate('/landing');
  };

  // 处理登录按钮点击
  const handleLoginClick = () => {
    setShowLoginModal(true);
  };

  // 处理登录成功
  const handleLoginSuccess = (userData) => {
    console.log('登录成功，用户数据:', userData);
    setShowLoginModal(false);
    // 这里可以根据需要跳转到特定页面
  };

  // 处理邀请码验证成功
  const handleInviteSuccess = (result) => {
    console.log('邀请码验证成功:', result);
    // 可以在这里显示成功通知或刷新用户信息
  };
  
  return (
    <>
      <AppBar 
        position="sticky" 
        elevation={0}
        sx={{
          borderBottom: 'none',
          py: 0.5,
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
          background: 'white',
          padding: 0,
          color: theme.palette.primary.main
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ justifyContent: 'space-between' }}>
            {/* Logo */}
            <Box
              sx={{ 
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer'
              }}
              onClick={() => {
                navigate('/landing');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              {/* SVG Logo - 提取的流畅曲线 */}
              <svg
                width="36"
                height="24"
                viewBox="0 0 1024 300"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M566.784 221.76c-2.048 4.096-4.096 7.68-5.632 11.776-15.36 23.04-27.648 47.616-36.864 72.704-12.8 36.352-5.632 68.608 31.232 81.408 66.048 20.48 137.216 17.408 201.216-8.704 16.896-5.632 21.504-8.192 64.512-24.576 31.232-12.8 62.976-24.064 95.744-32.256 2.048-0.512 4.096-1.024 5.632-2.048 18.432-3.584 12.8-10.752 31.744 2.048 7.168 5.632 16.384 7.168 25.088 5.632 8.704-2.048 10.24-5.632 15.872-14.336 10.24-15.36 6.144-36.352-8.704-47.104-35.84-24.576-42.496-18.944-77.824-11.776-2.56 1.024-5.632 1.536-7.68 2.048-35.84 9.216-71.168 20.992-104.448 35.328-40.96 16.384-45.568 17.92-61.44 23.552-46.592 19.456-97.792 23.552-147.456 11.776 8.704-20.992 18.944-40.96 31.232-60.416 2.56-3.584 4.608-8.192 7.168-11.776 16.384-26.112 28.16-54.272 34.816-84.48 8.192-36.352-14.848-72.192-50.688-80.384-1.536-0.512-2.56-0.512-4.096-1.024-51.712-12.8-112.128 5.632-280.576 71.168-154.112 60.416-221.696 79.36-243.2 67.072-7.68-4.608-16.384-5.632-25.088-2.56-17.92 5.12-28.672 24.064-23.552 41.472 2.56 8.192 7.68 15.872 15.872 19.968 43.52 16.384 92.16 14.848 134.656-3.584 56.32-16.896 112.128-37.376 165.888-60.416 146.432-57.856 208.896-76.288 239.104-68.608 5.632 2.048 5.632 0 4.608 5.632-5.632 22.528-14.336 43.52-27.136 62.464z"
                  fill="currentColor"
                  stroke="currentColor"
                  strokeWidth="12"
                  strokeLinejoin="round"
                />
              </svg>
            </Box>
            
            {/* 右侧区域：导航菜单 + 用户信息/登录按钮 */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* 导航菜单 */}
              <Tabs
                value={getCurrentTab()}
                onChange={handleTabChange}
                sx={{
                  '& .MuiTabs-indicator': {
                    backgroundColor: theme.palette.primary.main,
                    height: 2,
                    borderRadius: '5px 5px 0 0',
                    boxShadow: '0 2px 8px rgba(67, 83, 255, 0.4)',
                  },
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '1rem',
                    color: theme.palette.text.secondary,
                    minWidth: 80,
                    minHeight: 64,
                    padding: '12px 12px',
                    '&.Mui-selected': {
                      color: theme.palette.primary.main,
                      fontWeight: 600
                    },
                    '&:hover': {
                      color: theme.palette.primary.main,
                      opacity: 0.8
                    }
                  }
                }}
              >
                {navigationItems.map((item, index) => (
                  <Tab
                    key={index}
                    label={item.label}
                    sx={{ mx: 0.5 }}
                  />
                ))}
              </Tabs>
              
              {/* 用户信息或登录按钮 */}
              {isAuthenticated && user ? (
                <Box sx={{ display: 'flex', alignItems: 'center', paddingLeft: '20px' }}>
                  <Box 
                    sx={{ 
                      backgroundColor: 'rgb(245, 245, 245)',
                      px: 2, 
                      py: 0.5, 
                      borderRadius: 4,
                      mr: 2,
                      display: { xs: 'none', sm: 'flex' }
                    }}
                  >
                    <Typography variant="body2" sx={{ color: theme.palette.primary.main, fontWeight: 'medium' }}>
                      {user.points} 点数
                    </Typography>
                  </Box>
                  
                  <Tooltip title="账户设置">
                    <Button
                      onClick={handleOpenMenu}
                      endIcon={<ArrowDropDownIcon sx={{ color: theme.palette.primary.main }} />}
                      sx={{ 
                        textTransform: 'none',
                        color: theme.palette.primary.main,
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar 
                          alt={user.username}
                          src={user.avatar || ''}
                          sx={{ 
                            width: 32, 
                            height: 32, 
                            mr: 1,
                            bgcolor: theme.palette.primary.main,
                            color: 'white'
                          }}
                        >
                          {user.username.charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography variant="subtitle2" sx={{ display: { xs: 'none', md: 'block' }, color: theme.palette.primary.main }}>
                          {user.username}
                        </Typography>
                      </Box>
                    </Button>
                  </Tooltip>
                  
                  <Menu
                    anchorEl={anchorEl}
                    id="account-menu"
                    open={Boolean(anchorEl)}
                    onClose={handleCloseMenu}
                    PaperProps={{
                      elevation: 2,
                      sx: {
                        overflow: 'visible',
                        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.08))',
                        mt: 1.5,
                        borderRadius: 2,
                        minWidth: 180,
                        '& .MuiMenuItem-root': {
                          px: 2,
                          py: 1.5,
                          borderRadius: 1,
                          mx: 1,
                          my: 0.5,
                          '&:hover': {
                            backgroundColor: theme.palette.background.light,
                          },
                        },
                      },
                    }}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  >
                    <MenuItem onClick={handleProfile}>
                      <PersonIcon sx={{ mr: 1, fontSize: 20 }} />
                      个人中心
                    </MenuItem>
                    {eligible && (
                      <MenuItem onClick={handleInviteCode}>
                        <CardGiftcardIcon sx={{ mr: 1, fontSize: 20 }} />
                        填写邀请码
                      </MenuItem>
                    )}
                    <MenuItem onClick={handleLogout}>
                      <LogoutIcon sx={{ mr: 1, fontSize: 20 }} />
                      退出登录
                    </MenuItem>
                  </Menu>
                </Box>
              ) : (
                <Box sx={{ paddingLeft: '20px' }}>
                  <Button
                    variant="contained"
                    startIcon={<LoginIcon />}
                    onClick={handleLoginClick}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 2.5,
                      py: 0.75,
                      borderRadius: 3,
                      boxShadow: '0 4px 12px rgba(67, 83, 255, 0.3)',
                      '&:hover': {
                        boxShadow: '0 6px 16px rgba(67, 83, 255, 0.4)',
                        transform: 'translateY(-1px)',
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    立即登录体验
                  </Button>
                </Box>
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* 登录弹窗 */}
      <LoginModal
        open={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLoginSuccess={handleLoginSuccess}
      />

      {/* 邀请码弹窗 */}
      <InviteCodeModal
        open={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onSuccess={handleInviteSuccess}
      />
    </>
  );
};

export default AppHeader; 